
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Login from '../Login';
import AccountCreation from '../AccountCreation';
import PasswordReset from './PasswordReset';
import { AuthResponse } from '../../types/user';

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  onAuthSuccess: (authResponse: AuthResponse) => void;
  initialMode?: 'login' | 'register' | 'reset';
}

export default function AuthDialog({ open, onClose, onAuthSuccess, initialMode = 'login' }: AuthDialogProps) {
  const getTabIndex = (mode: string) => {
    switch (mode) {
      case 'login': return 0;
      case 'register': return 1;
      case 'reset': return 2;
      default: return 0;
    }
  };

  const [activeTab, setActiveTab] = useState<number>(getTabIndex(initialMode));

  // Update tab when initialMode changes
  useEffect(() => {
    setActiveTab(getTabIndex(initialMode));
  }, [initialMode]);

  const handleAuthSuccess = (authResponse: AuthResponse) => {
    console.debug('Authentication successful:', authResponse);
    onAuthSuccess(authResponse);
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Authentication</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="authentication tabs"
            variant="fullWidth"
          >
            <Tab label="Sign In" />
            <Tab label="Create Account" />
            <Tab label="Reset Password" />
          </Tabs>
        </Box>
        <Box sx={{ p: 3 }}>
          {activeTab === 0 ? (
            <Login
              onLoginSuccess={handleAuthSuccess}
              onCancel={onClose}
              onSwitchToRegister={() => setActiveTab(1)}
              onSwitchToReset={() => setActiveTab(2)}
            />
          ) : activeTab === 1 ? (
            <AccountCreation
              onAccountCreated={handleAuthSuccess}
              onCancel={onClose}
              onSwitchToLogin={() => setActiveTab(0)}
            />
          ) : (
            <PasswordReset
              onCancel={onClose}
              onSwitchToLogin={() => setActiveTab(0)}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}